import React, { useCallback, useContext, useEffect, useState } from 'react';
import { AlertTriangle, Loader2, PackageSearch, Settings } from 'lucide-react';

import { InventoryAdjustmentApi } from 'api/inventoryAdjustment';
import { WarehouseInventoryV2Api } from 'api/warehouseInventoryV2';
import { UserContext } from 'contexts/UserContext';
import { Button } from 'shadcn/components/ui/Button';
import { Card, CardContent } from 'shadcn/components/ui/Card';
import { Tooltip, TooltipContent, TooltipTrigger } from 'shadcn/components/ui/tooltip';
import { HandsontableDataGrid } from 'shared/components/dataTableHandson';
import { ExportButton } from 'shared/components/ExportCSVButton';
import { Notifications } from 'shared/components/Notifications';
import dayjs from 'utils/dayjsConfig';

import { AddBulkShrinkModal } from './AddBulkShrinkModal';
import { ItemsFromWeekSelector } from './ItemsFromWeekSelector';
import { ShrinkShipmentModal } from './ShrinkShipmentModal';

const warehouseApi = new WarehouseInventoryV2Api();
const inventoryAdjustmentApi = new InventoryAdjustmentApi();

function PendingTab() {
    const { user } = useContext(UserContext);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [isOpen, setIsOpen] = useState(false);
    const [isWeekSelectorOpen, setIsWeekSelectorOpen] = useState(false);
    const [allEntries, setAllEntries] = useState([]);
    const [totalPendingEntries, setTotalPendingEntries] = useState(0);
    const [totalUnits, setTotalUnits] = useState(0);
    const [totalCost, setTotalCost] = useState(0);
    const [selectedRequests, setSelectedRequests] = useState([]);
    const [selectedRowIndices, setSelectedRowIndices] = useState([]);
    const [selectedSummaryString, setSelectedSummaryString] = useState('');
    const [itemInformation, setItemInformation] = useState([]);
    const [reasonCodesByCategory, setReasonCodesByCategory] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [isApproving, setIsApproving] = useState(false);
    const [isRejecting, setIsRejecting] = useState(false);
    const [searchIndexes, setSearchIndexes] = useState(null);
    const [noItemsWarning, setNoItemsWarning] = useState(false);
    const [isShrinkShipmentModalOpen, setIsShrinkShipmentModalOpen] = useState(false);

    const columnHeaders = [
        'Sku',
        'Item Name',
        'Units',
        'Cost (Estimated)',
        'Category',
        'Reason Code',
        'Notes',
        'Username',
        'Date Entered',
        'Status',
    ];

    const columns = [
        {
            data: 'externalSku',
            type: 'autocomplete',
            source: (query, callback) => {
                const results = handleSearch(query);
                callback(results.map((item) => item.sku));
            },
            strict: false,
            width: 208.33,
        },
        {
            data: 'itemName',
            type: 'autocomplete',
            source: (query, callback) => {
                const results = handleSearch(query);
                callback(results.map((item) => item.itemName));
            },
            strict: false,
            width: 208.33,
        },
        { data: 'qtyUnits', type: 'numeric', readOnly: false, allowInvalid: false, width: 115.33 },
        {
            data: 'cost',
            type: 'numeric',
            numericFormat: { pattern: '$0,0.00', culture: 'en-US' },
            readOnly: true,
            allowInvalid: false,
            width: 208.33,
        },
        { data: 'category', type: 'text', readOnly: true, width: 208.33 },
        {
            data: 'reason',
            type: 'dropdown',
            source(query, callback) {
                try {
                    const rowIndex = this?.row;
                    const category = this?.instance?.getDataAtRowProp(rowIndex, 'category');
                    const reasons = getReasonsForCategory(category);
                    callback(Array.isArray(reasons) ? reasons : []);
                } catch (e) {
                    callback([]);
                }
            },
            width: 208.33,
            strict: true,
            allowInvalid: false,
            filter: false,
        },
        { data: 'notes', type: 'text', readOnly: false, width: 208.33 },
        { data: 'createdBy', type: 'text', readOnly: true, width: 208.33 },
        { data: 'createdOn', type: 'date', readOnly: true, allowInvalid: false, width: 208.33 },
        { data: 'status', type: 'text', readOnly: true, width: 208.33 },
    ];

    const getPendingBulkShrinkRequests = async (setLoading = false) => {
        if (setLoading) {
            setIsLoading(true);
        }
        const result = await inventoryAdjustmentApi.getPendingBulkShrinkRequests(user.facilityID);
        if (!result.status) {
            setNotification({ text: 'Error fetching pending bulk shrink requests', level: 'error' });
            return;
        }
        const sortedRows = result.data.rows.sort((a, b) => new Date(a.createdOn) - new Date(b.createdOn));
        setAllEntries(sortedRows);
        // Clear any selections after data reload so stale selections don't persist
        setSelectedRequests([]);
        setSelectedRowIndices([]);
        if (setLoading) {
            setIsLoading(false);
        }
    };

    const getItems = async (week, year) => {
        setIsLoading(true);
        let targetWeek = week;
        let targetYear = year;
        if (targetWeek == null || targetYear == null) {
            targetWeek = dayjs().isoWeek();
            targetYear = dayjs().isoWeekYear();
        }
        try {
            const result = await inventoryAdjustmentApi.getItemsForFacilityWeekYear(
                user.facilityID,
                targetWeek,
                targetYear,
            );
            if (!result.status) {
                setNotification({ text: 'Error fetching item information', level: 'error' });
                return;
            }
            const items = await mapItems(result.data?.items ?? []);
            if (items.length === 0) {
                setNoItemsWarning(true);
            } else {
                setNoItemsWarning(false);
            }
            setItemInformation(items);
        } finally {
            setIsLoading(false);
        }
    };

    const mapItems = async (ii) => {
        const items = ii.reduce((acc, item) => {
            if (!item.name || !item.externalSku) {
                return acc;
            }
            let category;
            if (item.l1TaxonomyId === 1) {
                category = 'PRODUCE';
            } else if (item.l1TaxonomyId === 1871) {
                category = 'FBM';
            } else if (item.l1TaxonomyId > 1 && item.l1TaxonomyId !== 1871) {
                category = 'GROCERY';
            } else {
                return acc; // catches null, 0, undefined, negatives, etc.
            }

            acc.push({
                sku: item.externalSku,
                itemName: item.name,
                category,
            });

            return acc;
        }, []);

        // Create search indexes after mapping items
        setSearchIndexes(createSearchIndexes(items));
        return items;
    };

    const createSearchIndexes = (items) => {
        const skuMap = new Map();
        const nameIndex = new Map();

        items.forEach((item) => {
            // Convert SKU to string for consistent handling
            const skuString = String(item.sku);
            skuMap.set(skuString, item);

            // Index by name substrings
            const words = item.itemName.toLowerCase().split(' ');
            words.forEach((word) => {
                if (!nameIndex.has(word)) {
                    nameIndex.set(word, new Set());
                }
                nameIndex.get(word).add(item);
            });
        });

        return { skuMap, nameIndex };
    };

    const searchItems = (query, indexes) => {
        if (!query) return [];

        const { nameIndex, skuMap } = indexes;
        const searchTerm = String(query).trim();

        if (skuMap.has(searchTerm)) {
            return [skuMap.get(searchTerm)];
        }

        const results = new Set();

        for (const [skuString, item] of skuMap.entries()) {
            if (skuString.startsWith(searchTerm)) {
                results.add(item);
            }
        }

        const searchWords = searchTerm.toLowerCase().split(' ');
        searchWords.forEach((word) => {
            nameIndex.forEach((items, indexWord) => {
                if (indexWord.includes(word)) {
                    items.forEach((item) => results.add(item));
                }
            });
        });

        return Array.from(results);
    };

    const handleSearch = (query) => {
        if (!query || !searchIndexes) return [];
        return searchItems(query, searchIndexes);
    };

    const getReasonCodes = async () => {
        const result = await warehouseApi.getAdjustReasons(null, true);
        if (!result.msg == 'Success' || !result.data?.reasons) {
            setNotification({ text: 'Error fetching reason codes', level: 'error' });
            return;
        }
        setReasonCodesByCategory(result.data.reasons);
    };

    useEffect(() => {
        getPendingBulkShrinkRequests();
        getItems();
        getReasonCodes();
    }, []);

    useEffect(() => {
        setTotalPendingEntries(allEntries.length);
        setTotalUnits(allEntries.reduce((acc, entry) => acc + entry.qtyUnits, 0));
        setTotalCost(allEntries.reduce((acc, entry) => acc + Number(entry?.cost ?? 0), 0));
    }, [allEntries]);

    useEffect(() => {
        handleSelectedSummaryString();
    }, [selectedRequests]);

    const handleOpenChange = (open, reload = false) => {
        if (reload) {
            getPendingBulkShrinkRequests(reload);
        }
        setIsOpen(open);
    };

    const handleShrinkShipmentModalOpenChange = (open, reload = false) => {
        if (reload) {
            getPendingBulkShrinkRequests(reload);
        }
        setIsShrinkShipmentModalOpen(open);
    };

    const handleSelectedSummaryString = () => {
        if (selectedRequests.length > 0) {
            const entries = `${selectedRequests.length} entries selected    `;
            const totalUnitsSelected = selectedRequests.reduce((acc, entry) => acc + Number(entry?.qtyUnits ?? 0), 0);

            const totalCostSelected = selectedRequests.reduce((acc, entry) => acc + Number(entry?.cost ?? 0), 0);
            const units = `${totalUnitsSelected} units`;
            const cost = `${totalCostSelected.toFixed(2)}`;
            setSelectedSummaryString(`${entries} | ${units} | $${cost}`);
        } else {
            setSelectedSummaryString('');
        }
    };

    const handleSelectionChange = (indices, rows) => {
        setSelectedRowIndices(indices);
        setSelectedRequests(rows);
    };

    const handleUpdateEntry = async (rowData, prop, oldValue, newValue) => {
        const result = await inventoryAdjustmentApi.updatePendingBulkShrinkRequest(rowData.id, prop, newValue);
        if (!result.status) {
            return false;
        }
        setNotification({ text: `Successfully updated ${prop}`, level: 'success' });
        return true;
    };

    const handleApproveEntries = useCallback(async (selectedEntries) => {
        setIsApproving(true);
        const ids = selectedEntries.map((entry) => entry.id);
        const result = await inventoryAdjustmentApi.approveBulkShrinkRequests(ids);
        if (!result.status) {
            setNotification({ text: 'Error approving bulk shrink requests', level: 'error' });
            setIsApproving(false);
            return;
        }
        const successCount = result?.data?.success?.length ?? 0;
        const failedCount = result?.data?.failed?.length ?? 0;

        if (successCount === 0 && failedCount > 0) {
            setNotification({ text: 'Error approving bulk shrink requests', level: 'error' });
            setIsApproving(false);
            return;
        }
        if (successCount > 0 && failedCount > 0) {
            setNotification({
                text: 'Some bulk shrink requests were approved, but some were rejected',
                level: 'warning',
            });
        } else {
            setNotification({ text: 'Bulk shrink requests approved', level: 'success' });
        }
        getPendingBulkShrinkRequests(true);
        setIsApproving(false);
    }, []);

    const handleRejectEntries = useCallback(async (selectedEntries) => {
        setIsRejecting(true);
        const ids = selectedEntries.map((entry) => entry.id);
        const result = await inventoryAdjustmentApi.rejectBulkShrinkRequests(ids);
        if (!result.status) {
            setNotification({ text: 'Error rejecting bulk shrink requests', level: 'error' });
            setIsRejecting(false);
            return;
        }
        setNotification({ text: 'Bulk shrink requests rejected', level: 'success' });
        getPendingBulkShrinkRequests(true);
        setIsRejecting(false);
    }, []);

    const getReasonsForCategory = useCallback(
        (category) => reasonCodesByCategory[category] || [],
        [reasonCodesByCategory],
    );

    const afterChange = useCallback(
        function (changes, source) {
            if (!changes || source === 'loadData' || source === 'updateData' || source === 'formatDate') {
                return;
            }

            if (source !== 'auto-update') {
                const hot = this;
                (async () => {
                    for (const [row, prop, oldValue, newValue] of changes) {
                        if (newValue == null || newValue === '') continue;
                        // Skip if the new value is the same as the old value
                        if (newValue === oldValue) continue;

                        if (prop === 'itemName' || prop === 'externalSku') {
                            const results = handleSearch(newValue);
                            const exact = results.find(
                                (i) => i.itemName === newValue || String(i.sku) === String(newValue),
                            );

                            if (exact) {
                                const previousItemName = hot.getDataAtRowProp(row, 'itemName');
                                const previousExternalSku = String(hot.getDataAtRowProp(row, 'externalSku') ?? '');
                                const previousCategory = hot.getDataAtRowProp(row, 'category');
                                const previousReason = hot.getDataAtRowProp(row, 'reason');

                                const currentCategory = previousCategory;

                                // Block if the change would alter the category (only when a current category exists)
                                if (
                                    currentCategory &&
                                    exact.category &&
                                    String(exact.category) !== String(currentCategory)
                                ) {
                                    // Revert the user edit and notify
                                    hot.setDataAtRowProp(row, prop, oldValue, 'auto-update');
                                    setNotification({
                                        text: 'Changing item or SKU to a different category is prohibited.',
                                        level: 'error',
                                    });
                                    continue;
                                }

                                // Apply linked field updates (safe: same category or no current category)
                                if (prop === 'itemName' && exact.itemName === newValue) {
                                    hot.setDataAtRowProp(row, 'externalSku', exact.sku, 'auto-update');
                                    hot.setDataAtRowProp(row, 'category', exact.category, 'auto-update');
                                } else if (prop === 'externalSku' && String(exact.sku) === String(newValue)) {
                                    hot.setDataAtRowProp(row, 'itemName', exact.itemName, 'auto-update');
                                    hot.setDataAtRowProp(row, 'category', exact.category, 'auto-update');
                                }

                                // Trigger update callback for the valid user edit
                                const updatedRowData = hot.getSourceDataAtRow(row);
                                const newExternalSku = String(exact.sku);
                                let success = true;
                                const colIndexForMeta = hot.propToCol('externalSku');
                                // Mark cell as updating and re-render
                                hot.setCellMeta(row, colIndexForMeta, 'isUpdating', true);
                                hot.render();
                                try {
                                    const result = await handleUpdateEntry(
                                        updatedRowData,
                                        'externalSku',
                                        previousExternalSku,
                                        newExternalSku,
                                    );
                                    // Treat undefined return as success for now
                                    success = result === undefined ? true : Boolean(result);
                                } catch (e) {
                                    success = false;
                                } finally {
                                    hot.setCellMeta(row, colIndexForMeta, 'isUpdating', false);
                                    hot.render();
                                }

                                if (!success) {
                                    // Revert all linked fields
                                    hot.setDataAtRowProp(row, 'itemName', previousItemName, 'auto-update');
                                    hot.setDataAtRowProp(row, 'externalSku', previousExternalSku, 'auto-update');
                                    hot.setDataAtRowProp(row, 'category', previousCategory, 'auto-update');
                                    hot.setDataAtRowProp(row, 'reason', previousReason, 'auto-update');
                                    setNotification({
                                        text: 'Failed to update.',
                                        level: 'error',
                                    });
                                }
                            }
                        } else {
                            // For non-item fields, pass through the original change
                            const updatedRowData = hot.getSourceDataAtRow(row);
                            let success = true;
                            const colIndexForMeta = hot.propToCol(prop);
                            hot.setCellMeta(row, colIndexForMeta, 'isUpdating', true);
                            hot.render();
                            try {
                                const result = await handleUpdateEntry(updatedRowData, prop, oldValue, newValue);
                                success = result === undefined ? true : Boolean(result);
                            } catch (e) {
                                success = false;
                            } finally {
                                hot.setCellMeta(row, colIndexForMeta, 'isUpdating', false);
                                hot.render();
                            }
                            if (!success) {
                                hot.setDataAtRowProp(row, prop, oldValue, 'auto-update');
                                setNotification({
                                    text: 'Failed to update entry. Reverting your change.',
                                    level: 'error',
                                });
                            }
                        }
                    }
                })();
            }
        },
        [handleSearch, handleUpdateEntry],
    );

    return (
        <div>
            <Notifications options={notification} />
            <div className="w-full max-w-[1200px] mb-4 mx-auto">
                <div className="flex items-center justify-start mb-2 gap-2">
                    <Button
                        variant="ghost"
                        size="icon"
                        aria-label="Select week and year"
                        onClick={() => setIsWeekSelectorOpen(true)}
                    >
                        <Settings className="h-5 w-5" />
                    </Button>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => setIsShrinkShipmentModalOpen(true)}
                                disabled={isLoading}
                                aria-label="Shrink a Shipment"
                            >
                                {isLoading ? (
                                    <Loader2 className="h-5 w-5 animate-spin" />
                                ) : (
                                    <PackageSearch className="h-5 w-5" />
                                )}
                            </Button>
                        </TooltipTrigger>
                        <TooltipContent>Shrink a Shipment</TooltipContent>
                    </Tooltip>
                    {noItemsWarning && (
                        <div className="flex items-center" role="alert">
                            <AlertTriangle className="h-4 w-4 text-red-600 mr-2" aria-hidden="true" />
                            <p className="text-sm text-red-600">
                                No items found for the selected week and year. Please select a different week and year.
                            </p>
                        </div>
                    )}
                </div>
                <div className="flex flex-row flex-wrap gap-4 mb-4">
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">{totalPendingEntries}</h1>
                                <h2 className="text-sm text-gray-500">Pending Entries</h2>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">{totalUnits}</h1>
                                <h2 className="text-sm text-gray-500">Total Units</h2>
                            </div>
                        </CardContent>
                    </Card>
                    <Card className="flex-1">
                        <CardContent>
                            <div className="flex flex-col gap-4 justify-center items-center">
                                <h1 className="text-2xl font-bold">${totalCost.toFixed(2)}</h1>
                                <h2 className="text-sm text-gray-500">Total Cost</h2>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                <div className="flex flex-row justify-between gap-4">
                    <Button onClick={() => setIsOpen(true)} disabled={isLoading}>
                        {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : 'Add Entries'}
                    </Button>
                    <div className="flex gap-4">
                        <h2 className="text-sm text-gray-500">{selectedSummaryString}</h2>
                        <Button
                            disabled={selectedRequests.length === 0 || isApproving}
                            onClick={() => handleApproveEntries(selectedRequests)}
                        >
                            {isApproving ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Approving...
                                </>
                            ) : (
                                'Approve Selected Entries'
                            )}
                        </Button>
                        <Button
                            disabled={selectedRequests.length === 0 || isRejecting}
                            onClick={() => handleRejectEntries(selectedRequests)}
                        >
                            {isRejecting ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Rejecting...
                                </>
                            ) : (
                                'Reject Selected Entries'
                            )}
                        </Button>
                        <ExportButton
                            data={allEntries}
                            columnHeaders={columnHeaders}
                            columns={columns}
                            disabled={allEntries.length === 0}
                            filename="shrink-log-pending-entries"
                        />
                    </div>
                </div>
            </div>
            <div>
                {isLoading ? (
                    <Card className="w-full max-w-[1200px] mx-auto">
                        <CardContent className="flex flex-col items-center justify-center py-8">
                            <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {allEntries.length === 0 ? (
                            <Card className="w-full max-w-[1200px] mx-auto">
                                <CardContent className="flex flex-col items-center justify-center py-8">
                                    <p className="text-gray-500 text-lg">No pending bulk shrink requests to show</p>
                                    <p className="text-gray-400 text-sm mt-2">
                                        Click Add Entries to create new bulk shrink requests
                                    </p>
                                </CardContent>
                            </Card>
                        ) : (
                            <div className="w-full overflow-x-auto">
                                <HandsontableDataGrid
                                    data={allEntries}
                                    selectable
                                    selectedRows={selectedRowIndices}
                                    onSelectionChange={handleSelectionChange}
                                    colHeaders={columnHeaders}
                                    columns={columns}
                                    afterChange={afterChange}
                                    handsontableSettings={{
                                        height: 400,
                                        manualColumnSize: false,
                                        rowHeaders: false,
                                        contextMenu: {
                                            items: {
                                                approve: {
                                                    name: 'Approve',
                                                    callback: (key, selection) => {
                                                        const rowData = allEntries[selection[0].start.row];
                                                        handleApproveEntries([rowData]);
                                                    },
                                                    disabled: false,
                                                },
                                                separator1: '---------',
                                                reject: {
                                                    name: 'Reject',
                                                    callback: (key, selection) => {
                                                        const rowData = allEntries[selection[0].start.row];
                                                        handleRejectEntries([rowData]);
                                                    },
                                                    disabled: false,
                                                },
                                            },
                                        },
                                    }}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            <ShrinkShipmentModal
                isOpen={isShrinkShipmentModalOpen}
                onOpenChange={handleShrinkShipmentModalOpenChange}
            />
            <AddBulkShrinkModal
                isOpen={isOpen}
                onOpenChange={handleOpenChange}
                getReasonsForCategory={getReasonsForCategory}
                searchItems={handleSearch}
                adjustmentApi={inventoryAdjustmentApi}
            />
            <ItemsFromWeekSelector
                isOpen={isWeekSelectorOpen}
                onOpenChange={setIsWeekSelectorOpen}
                onSubmit={({ week, year }) => getItems(week, year)}
            />
        </div>
    );
}

export { PendingTab };
